import { Box, Button, CardMedia, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Item, ItemDetails } from './index';
import {
  ApplicationProps,
  DocumentType,
  Document as ApiDocument,
} from '../../redux-store/types/api-types';
import { SubHeading } from '../investors/components';
import { ENABLE_KRA_DOCUMENT_FETCH, USER_ROLES } from '../../utils/constant';
import {
  checkKraBasedOnStatus,
  checkNDPMSForIndusindBank,
  documentTypeValidationCheckForDocumentNumberDocumentExpiryDate,
  documentTypeValidationCheckForDocumentNumberDocumentExpiryDateNominee,
  mergeDocumentsWithCustodian,
} from '../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { refLoginCheck } from '../Investments/investor-application';
import { KeyValue } from './contributor';
import { formatShortDate } from '../../utils/date';
import { getDocuments } from '../../redux-store/actions/application';
import { getDocAndCustodianData } from '../investors/documentDetails';

export const getFieldTitle: { [key: string]: string } = {
  addressProof: 'Address Proof',
  identityProof: 'Identity Proof',
  bankAccountProof: 'Bank Account Proof',
  poaNotarized: 'Poa Notarized Document',
  investorsConsent: 'Investors Consent Document',
  fatca: 'Fatca Document',
  form_10ff_trc: 'Form 10F/TRC',
  additional: 'Additional Document',
  investorPhotograph: 'Investor Photograph',
  investorSignature: 'Investor Signature',
  investorOldSignature: 'Old Signature',
  compliance_document: 'Compliance Document',
  pioCard: 'PIO CARD',
  overseaseAddressProof: 'Overseas Address Proof',
  passportCopy: 'Passport Copy (1st and last page)',
  visaCopy: 'Visa Copy',
  workPermit: 'Work Permit/Resident Card',
  nomineeIdProof: 'Nominee ID Proof',
  guardianIdProof: 'Guardian ID Proof',
  otherdocument1: 'Other Document 1',
  otherdocument2: 'Other Document 2',
  correspondenceAddress: 'Correspondence Address',
  newBankAccountProof: 'New Bank Account Proof',
  panCardCopy: 'PAN Card Copy',
};

export default function DocumentDetails({
  application,
}: {
  application: ApplicationProps;
}): JSX.Element {
  const { applicants, nominees } = application;
  const { role = '', id: authId = '' } = useSelector((store: RootStateType) => store.auth);
  const referenceDetail = useSelector((store: RootStateType) => store.refrenceIdReducer);
  const dispatch = useDispatch();
  const [documentsData, setDocumentsData] = useState<ApiDocument | null>(null);

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getDocuments())) as any;
        setDocumentsData(response);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);
  return (
    <>
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: '10px',
        }}
        mt={5}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Typography
              component="h2"
              sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main' }}>
              Document Details
            </Typography>
            {applicants.map((applicant, index: number) => {
              const { custodian, hasPOA } = application;
              const checkNdpmsFlow = checkNDPMSForIndusindBank(
                application?.accountType,
                application?.bankDetails
              );
              const { docData, custodianDoc } = getDocAndCustodianData(
                custodian,
                applicant,
                hasPOA,
                checkNdpmsFlow
              );
              const mergedDocuments =
                documentsData &&
                (mergeDocumentsWithCustodian(
                  (documentsData as ApiDocument)[docData as keyof ApiDocument] || [],
                  (documentsData as ApiDocument)[custodianDoc as keyof ApiDocument] || [],
                  custodian
                ) as unknown as DocumentType[]);
              const { documents = [] } = applicant;
              const updatedDocuments = mergedDocuments
                ? (mergedDocuments
                    .filter((doc) => doc.documentType !== 'typedDeclaration')
                    .reduce((prev: DocumentType[], curr) => {
                      const { documentType } = curr;
                      const modifiedDocTypes = prev.map((doc) => doc.documentType);
                      const docsOfCurrentDocType = documents.filter(
                        (doc) =>
                          doc.documentType === documentType &&
                          !modifiedDocTypes.includes(documentType)
                      );
                      const updatedDocs =
                        docsOfCurrentDocType.length > 1
                          ? docsOfCurrentDocType.map((doc, index) => ({
                              ...doc,
                              documentName: `${doc.documentName} (${index + 1})`,
                            }))
                          : docsOfCurrentDocType;
                      return [...prev, ...updatedDocs];
                    }, []) as DocumentType[])
                : [];

              const title = [
                'First Applicant Details',
                'Second Applicant Details',
                'Third Applicant Details',
              ][index];
              return (
                <React.Fragment key={applicant.id}>
                  {updatedDocuments && updatedDocuments.length !== 0 && (
                    <SubHeading sx={{ letterSpacing: 0.5 + 'px' }}>{title}</SubHeading>
                  )}
                  {ENABLE_KRA_DOCUMENT_FETCH &&
                    !refLoginCheck(
                      authId,
                      application as unknown as ApplicationProps,
                      referenceDetail
                    ) &&
                    USER_ROLES.INVESTOR !== role &&
                    updatedDocuments &&
                    updatedDocuments.length === 0 &&
                    checkKraBasedOnStatus(application.status) && (
                      <SubHeading sx={{ letterSpacing: 0.5 + 'px' }}>{title}</SubHeading>
                    )}
                  {ENABLE_KRA_DOCUMENT_FETCH &&
                  !refLoginCheck(
                    authId,
                    application as unknown as ApplicationProps,
                    referenceDetail
                  ) &&
                  USER_ROLES.INVESTOR !== role
                    ? updatedDocuments &&
                      updatedDocuments.length === 0 &&
                      index === 0 &&
                      !applicant.kraDocument &&
                      !checkKraBasedOnStatus(application.status) && (
                        <Grid
                          xs={12}
                          sm={12}
                          sx={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: 'common.black',
                            textAlign: 'center',
                            my: 3,
                          }}>
                          No Documents
                        </Grid>
                      )
                    : updatedDocuments &&
                      updatedDocuments.length === 0 &&
                      index === 0 && (
                        <Grid
                          xs={12}
                          sm={12}
                          sx={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: 'common.black',
                            textAlign: 'center',
                            my: 3,
                          }}>
                          No Documents
                        </Grid>
                      )}
                  <Grid
                    container
                    pt={updatedDocuments && updatedDocuments.length && 2}
                    pb={updatedDocuments && updatedDocuments.length && 5}
                    columnSpacing={13}
                    sx={{ pl: { xs: 0, sm: 7, md: 10, lg: 13 } }}>
                    {updatedDocuments &&
                      updatedDocuments.map((document, index: number) => {
                        return (
                          <React.Fragment key={document.id}>
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={document.file ? 6 : 12}
                              sx={{ mt: index !== 0 ? 6 : 0 }}>
                              <Item>{getFieldTitle[document.documentType]}</Item>
                              {/* {document.documentType === 'investorSignature' && (
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: 14,
                                  }}>
                                  (This signature copy is used as a verification process for future
                                  redemptions and transfer requests)
                                </Typography>
                              )} */}
                              <ItemDetails>
                                {document.documentName?.toLowerCase() ===
                                getFieldTitle[document.documentType]?.toLowerCase()
                                  ? document.file?.name
                                  : document.documentName}
                              </ItemDetails>
                            </Grid>
                            {document.file && (
                              <Grid item xs={12} sm={6}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                    flexDirection: { sm: 'column', xs: 'row', md: 'row' },
                                  }}>
                                  <Button
                                    aria-label={`View ${document?.documentName || 'Document'}`}
                                    variant="contained"
                                    sx={{
                                      color: 'common.white',
                                      // minWidth: { xs: '90%', sm: '100%', md: '70%' },
                                      minWidth: '70%',
                                      // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                      fontWeight: 600,
                                      lineHeight: 1.5,
                                    }}
                                    onClick={() => window.open(document.file?.viewUrl)}>
                                    View
                                  </Button>
                                  <Tooltip title={'Download'}>
                                    <IconButton
                                      aria-label={`Download ${
                                        document?.documentName || 'Document'
                                      }`}
                                      sx={{
                                        // p: 0,
                                        // pl: 1.5,
                                        ':hover': {
                                          bgcolor: '#F4FCFC',
                                        },
                                        cursor: 'pointer',
                                        pointerEvents: 'all',
                                        // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                      }}
                                      onClick={() => window.open(document.file?.url)}>
                                      <CardMedia
                                        component="img"
                                        src={'/images/download-icon.png'}
                                        alt="Download"
                                        sx={{ width: '25px' }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Grid>
                            )}

                            {documentTypeValidationCheckForDocumentNumberDocumentExpiryDate(
                              document.documentType
                            ) &&
                              document.documentNumber && (
                                <Grid item xs={12} sm={6} md={12} sx={{ mt: 0 }}>
                                  <KeyValue
                                    title={
                                      document.documentType === 'identityProof'
                                        ? 'Identification Number'
                                        : 'Document Number'
                                    }
                                    description={document.documentNumber}
                                  />
                                  {document.documentExpiryDate && (
                                    <KeyValue
                                      title={'Expiry Date'}
                                      description={formatShortDate(document.documentExpiryDate)}
                                    />
                                  )}
                                </Grid>
                              )}
                          </React.Fragment>
                        );
                      })}
                    {ENABLE_KRA_DOCUMENT_FETCH &&
                      USER_ROLES.INVESTOR !== role &&
                      !refLoginCheck(
                        authId,
                        application as unknown as ApplicationProps,
                        referenceDetail
                      ) &&
                      checkKraBasedOnStatus(application.status) &&
                      (applicant.kraDocument && applicant.kraDocument.length > 0 ? (
                        <>
                          {applicant.kraDocument.map((document, index: number) => {
                            return (
                              <React.Fragment key={document.id}>
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={document ? 6 : 12}
                                  sx={{
                                    mt: updatedDocuments && updatedDocuments.length === 0 ? 0 : 6,
                                  }}>
                                  <Item>{'KRA Documents'}</Item>
                                  <ItemDetails>{document.name}</ItemDetails>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Button
                                    variant="contained"
                                    sx={{
                                      color: 'common.white',
                                      minWidth: { xs: '90%', sm: '100%', md: '70%' },
                                      mt:
                                        updatedDocuments && updatedDocuments.length === 0
                                          ? 2
                                          : { sm: 8, xs: 2 },
                                      fontWeight: 600,
                                      lineHeight: 1.5,
                                    }}
                                    onClick={() => window.open(document.url)}>
                                    Download
                                  </Button>
                                </Grid>
                              </React.Fragment>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          <React.Fragment>
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={document ? 6 : 12}
                              sx={{
                                mt: updatedDocuments && updatedDocuments.length === 0 ? 0 : 6,
                              }}>
                              <Item>{'KRA Documents'}</Item>
                              <ItemDetails>{'KRA Documents in Progress'}</ItemDetails>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Button
                                variant="contained"
                                sx={{
                                  color: 'common.white',
                                  minWidth: { xs: '90%', sm: '100%', md: '70%' },
                                  mt:
                                    updatedDocuments && updatedDocuments.length === 0
                                      ? 2
                                      : { sm: 8, xs: 2 },
                                  fontWeight: 600,
                                  lineHeight: 1.5,
                                }}
                                disabled={true}
                                // onClick={() => window.open(document.url)}
                              >
                                Download
                              </Button>
                            </Grid>
                          </React.Fragment>
                        </>
                      ))}
                    {applicant.aadharXMLFromDigiLockerFileId && (
                      <React.Fragment>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={6}
                          sx={{
                            mt: updatedDocuments && updatedDocuments.length === 0 ? 0 : 6,
                          }}>
                          <Item>{'DigiLocker Aadhaar XML'}</Item>
                          <ItemDetails>{applicant?.aadharXMLFromDigiLockerFile?.name}</ItemDetails>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: 'flex',
                              mt: { sm: 8, xs: 2 },
                              flexDirection: { sm: 'column', xs: 'row', md: 'row' },
                            }}>
                            <Button
                              variant="contained"
                              sx={{
                                color: 'common.white',
                                // minWidth: { xs: '90%', sm: '100%', md: '70%' },
                                minWidth: '70%',
                                // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                fontWeight: 600,
                                lineHeight: 1.5,
                              }}
                              onClick={() =>
                                window.open(applicant?.aadharXMLFromDigiLockerFile?.viewUrl)
                              }>
                              View
                            </Button>
                            <Tooltip title={'Download'}>
                              <IconButton
                                sx={{
                                  // p: 0,
                                  // pl: 1.5,
                                  ':hover': {
                                    bgcolor: '#F4FCFC',
                                  },
                                  cursor: 'pointer',
                                  pointerEvents: 'all',
                                  // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                }}
                                onClick={() =>
                                  window.open(applicant?.aadharXMLFromDigiLockerFile?.url)
                                }>
                                <CardMedia
                                  component="img"
                                  src={'/images/download-icon.png'}
                                  alt="Download"
                                  sx={{ width: '25px' }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </React.Fragment>
                    )}
                  </Grid>
                </React.Fragment>
              );
            })}
            {nominees.length !== 0 &&
              role !== USER_ROLES.INVESTOR &&
              nominees.map((nominee, index: number) => {
                const { nomineedocuments = [] } = nominee;
                const updatedNomineeDocuments = nomineedocuments.reduce(
                  (prev: DocumentType[], curr) => {
                    const { documentType } = curr;
                    const modifiedDocTypes = prev.map((doc) => doc.documentType);
                    const docsOfCurrentDocType = nomineedocuments.filter(
                      (doc) =>
                        doc.documentType === documentType &&
                        !modifiedDocTypes.includes(documentType)
                    );
                    const updatedDocs =
                      docsOfCurrentDocType.length > 1
                        ? docsOfCurrentDocType.map((doc, index) => ({
                            ...doc,
                            documentName: `${doc.documentName} (${index + 1})`,
                          }))
                        : docsOfCurrentDocType;
                    return [...prev, ...updatedDocs];
                  },
                  []
                ) as DocumentType[];
                const title = [
                  'First Nominee Details',
                  'Second Nominee Details',
                  'Third Nominee Details',
                ][index];
                return (
                  <React.Fragment key={nominee.id}>
                    {updatedNomineeDocuments && updatedNomineeDocuments.length !== 0 && (
                      <SubHeading sx={{ letterSpacing: 0.5 + 'px' }}>{title}</SubHeading>
                    )}
                    <Grid
                      container
                      pt={updatedNomineeDocuments && updatedNomineeDocuments.length && 2}
                      pb={updatedNomineeDocuments && updatedNomineeDocuments.length && 5}
                      columnSpacing={13}
                      sx={{ pl: { xs: 0, sm: 7, md: 10, lg: 13 } }}>
                      {updatedNomineeDocuments &&
                        updatedNomineeDocuments.map((document, index: number) => {
                          return (
                            <React.Fragment key={document.id}>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={document.file ? 6 : 12}
                                sx={{ mt: index !== 0 ? 6 : 0 }}>
                                <Item>{getFieldTitle[document.documentType]}</Item>
                                <ItemDetails>
                                  {document.documentName?.toLowerCase() ===
                                  getFieldTitle[document.documentType]?.toLowerCase()
                                    ? document.file?.name
                                    : document.documentName}
                                </ItemDetails>
                              </Grid>
                              {document.file && (
                                <Grid item xs={12} sm={6}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                      flexDirection: { sm: 'column', xs: 'row', md: 'row' },
                                    }}>
                                    <Button
                                      variant="contained"
                                      sx={{
                                        color: 'common.white',
                                        // minWidth: { xs: '90%', sm: '100%', md: '70%' },
                                        minWidth: '70%',
                                        // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                        fontWeight: 600,
                                        lineHeight: 1.5,
                                      }}
                                      onClick={() => window.open(document.file?.viewUrl)}>
                                      View
                                    </Button>
                                    <Tooltip title={'Download'}>
                                      <IconButton
                                        sx={{
                                          // p: 0,
                                          // pl: 1.5,
                                          ':hover': {
                                            bgcolor: '#F4FCFC',
                                          },
                                          cursor: 'pointer',
                                          pointerEvents: 'all',
                                          // mt: index !== 0 ? { sm: 8, xs: 2 } : 2,
                                        }}
                                        onClick={() => window.open(document.file?.url)}>
                                        <CardMedia
                                          component="img"
                                          src={'/images/download-icon.png'}
                                          alt="Download"
                                          sx={{ width: '25px' }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                              )}
                              {documentTypeValidationCheckForDocumentNumberDocumentExpiryDateNominee(
                                document.documentType
                              ) &&
                                document.documentNumber && (
                                  <Grid item xs={12} sm={6} md={12} sx={{ mt: 0 }}>
                                    <KeyValue
                                      title={'Identification Number'}
                                      description={document.documentNumber}
                                    />
                                    {document.documentExpiryDate && (
                                      <KeyValue
                                        title={'Expiry Date'}
                                        description={formatShortDate(document.documentExpiryDate)}
                                      />
                                    )}
                                  </Grid>
                                )}
                            </React.Fragment>
                          );
                        })}
                    </Grid>
                  </React.Fragment>
                );
              })}
          </Grid>
        </Box>
      </Box>
    </>
  );
}
